import React, { useState, useMemo } from 'react';
import moment from 'moment';
import { Row, Col, FormGroup, Label, Input, Table } from 'reactstrap';
import ReactPaginate from 'react-paginate';
import Isvg from 'react-inlinesvg';
import Page from '../../containers/page';
import RouteComponent from '../../helpers/route';
import Button from '../../form-builder/fields/button';
import Swal from 'sweetalert2';
import sortUp from '../../assets/sortup.svg';
import sortDown from '../../assets/sortdown.svg';

const palette = {
    bg: '#F8F9FA',
    surface: '#FFFFFF',
    border: '#E6EDF5',
    borderStrong: '#D2D6DA',
    text: '#3A3F56',
    textMuted: '#818FA5',
    primary: '#33405E',
    primaryDark: '#2A2F35',
    accent: '#60BEBB',
    danger: '#E5484D',
    dangerSoft: '#FDECEC',
    warning: '#F5A623',
    warningSoft: '#FFF4E0',
    success: '#2DA160',
    successSoft: '#E6F4EC',
    infoSoft: '#EAEEF2',
    rowAlt: '#FAFBFC',
    code: '#F4F6F8',
};

const styles = {
    subtitle: { fontSize: 13, color: palette.textMuted, marginTop: 4 },
    btn: {
        height: 36, padding: '0 14px', backgroundColor: palette.primary, color: '#fff',
        border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer',
    },
    btnGhost: {
        height: 36, padding: '0 14px', backgroundColor: 'transparent', color: palette.text,
        border: `1px solid ${palette.borderStrong}`, borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer',
    },
    btnSuccess: {
        height: 36, padding: '0 14px', backgroundColor: palette.success, color: '#fff',
        border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer',
    },
    mono: { fontFamily: "'SFMono-Regular', Menlo, Monaco, Consolas, monospace", fontSize: 12 },
    badge: {
        display: 'inline-flex', alignItems: 'center', padding: '3px 8px', borderRadius: 12,
        fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3,
    },
    tag: { display: 'inline-block', padding: '2px 6px', borderRadius: 4, fontSize: 11, backgroundColor: palette.infoSoft, color: palette.text },
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(42,47,53,0.5)', zIndex: 998 },
    panel: {
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(640px, 92vw)',
        backgroundColor: palette.surface, zIndex: 999, boxShadow: '-10px 0 25px rgba(0,0,0,0.08)',
        display: 'flex', flexDirection: 'column',
    },
    panelHeader: {
        padding: '18px 22px', borderBottom: `1px solid ${palette.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    },
    panelBody: { flex: 1, overflowY: 'auto', padding: '18px 22px' },
    panelFooter: {
        padding: '14px 22px', borderTop: `1px solid ${palette.border}`,
        display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end',
    },
    section: { marginBottom: 28 },
    sectionTitle: {
        fontSize: 12, fontWeight: 700, color: palette.textMuted, textTransform: 'uppercase',
        letterSpacing: 0.6, marginBottom: 10,
    },
    kvGrid: { display: 'grid', gridTemplateColumns: '180px 1fr', rowGap: 8, columnGap: 12, fontSize: 13 },
    kvKey: { color: palette.textMuted, fontWeight: 500 },
    kvValue: { color: palette.text, wordBreak: 'break-word' },
    timelineItem: {
        position: 'relative', padding: '10px 12px 10px 14px', marginBottom: 8,
        backgroundColor: palette.code, borderRadius: 6, borderLeft: `3px solid ${palette.border}`,
    },
    timelineItemFailed: { borderLeft: `3px solid ${palette.danger}`, backgroundColor: palette.dangerSoft },
    timelineHeader: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 },
    iconOk: { color: palette.success, fontWeight: 700 },
    iconFail: { color: palette.danger, fontWeight: 700 },
    detailJson: {
        marginTop: 8, padding: 10, backgroundColor: palette.surface,
        border: `1px solid ${palette.border}`, borderRadius: 4,
        fontFamily: "'SFMono-Regular', Menlo, Monaco, Consolas, monospace",
        fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 320, overflow: 'auto',
    },
    closeBtn: { background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer', color: palette.textMuted, lineHeight: 1 },
};

const statusColors = {
    completed: { background: palette.successSoft, color: palette.success },
    manual_review: { background: palette.dangerSoft, color: palette.danger },
    processing: { background: palette.warningSoft, color: palette.warning },
};

const sourceColors = {
    refund: { background: '#E8F1FF', color: '#2C66C9' },
    order_edit: { background: '#F3EBFC', color: '#7E3FBF' },
};

function StatusBadge({ status }) {
    const s = statusColors[status] || { background: palette.infoSoft, color: palette.text };
    return (
        <span style={{ ...styles.badge, backgroundColor: s.background, color: s.color }}>
            {status ? status.replace('_', ' ') : '—'}
        </span>
    );
}

function SourceBadge({ source }) {
    const s = sourceColors[source] || { background: palette.infoSoft, color: palette.text };
    return (
        <span style={{ ...styles.badge, backgroundColor: s.background, color: s.color }}>
            {source || '—'}
        </span>
    );
}

function truncate(str, n = 12) {
    if (!str) return '';
    const s = String(str);
    if (s.length <= n) return s;
    return `${s.slice(0, n)}…`;
}

function formatDate(value) {
    if (!value) return '—';
    const m = moment(value);
    if (!m.isValid()) return String(value);
    return m.format('YYYY-MM-DD HH:mm');
}

function formatStepTime(value) {
    if (!value) return '—';
    const m = moment(value);
    if (!m.isValid()) return String(value);
    return m.format('HH:mm:ss.SSS');
}

function JsonBlock({ value, defaultOpen = false, label }) {
    const [open, setOpen] = useState(defaultOpen);
    const hasValue = value !== undefined && value !== null && !(typeof value === 'object' && Object.keys(value).length === 0);
    if (!hasValue) return <span style={{ color: palette.textMuted, fontSize: 12 }}>—</span>;
    return (
        <div>
            <button
                onClick={() => setOpen((v) => !v)}
                style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: palette.primary, fontSize: 12, fontWeight: 500 }}
            >
                {open ? '▾' : '▸'} {label || (open ? 'Hide details' : 'Show details')}
            </button>
            {open && (
                <pre style={styles.detailJson}>{JSON.stringify(value, null, 2)}</pre>
            )}
        </div>
    );
}

function StepsTimeline({ steps }) {
    const sorted = useMemo(() => {
        if (!Array.isArray(steps)) return [];
        return [...steps].sort((a, b) => {
            const ta = a && a.at ? new Date(a.at).getTime() : 0;
            const tb = b && b.at ? new Date(b.at).getTime() : 0;
            return ta - tb;
        });
    }, [steps]);

    if (!sorted.length) return <div style={{ color: palette.textMuted, fontSize: 13 }}>No steps recorded.</div>;

    return (
        <div>
            {sorted.map((step, idx) => {
                const failed = step && step.ok === false;
                return (
                    <div key={idx} style={{ ...styles.timelineItem, ...(failed ? styles.timelineItemFailed : {}) }}>
                        <div style={styles.timelineHeader}>
                            <span style={failed ? styles.iconFail : styles.iconOk}>{failed ? '✗' : '✓'}</span>
                            <span style={{ ...styles.mono, fontWeight: 700 }}>{step && step.step ? step.step : '(unnamed)'}</span>
                            <span style={{ color: palette.textMuted, fontSize: 12, marginLeft: 'auto' }}>{formatStepTime(step && step.at)}</span>
                        </div>
                        {step && step.detail !== undefined && step.detail !== null && (
                            <div style={{ marginTop: 6 }}>
                                <JsonBlock value={step.detail} label="detail" />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function DetailPanel({ event, onClose, onReset, onMarkResolved, busy }) {
    if (!event) return null;
    const a = event.attempts || {};
    return (
        <>
            <div style={styles.overlay} onClick={onClose} />
            <div style={styles.panel} role="dialog" aria-modal="true">
                <div style={styles.panelHeader}>
                    <div>
                        <div style={{ fontSize: 12, color: palette.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>Refund event</div>
                        <div style={{ ...styles.mono, fontSize: 14, marginTop: 2 }}>{event.eventId || event._id}</div>
                    </div>
                    <button style={styles.closeBtn} onClick={onClose} aria-label="Close">×</button>
                </div>

                <div style={styles.panelBody}>
                    <div style={styles.section}>
                        <div style={styles.sectionTitle}>Event details</div>
                        <div style={styles.kvGrid}>
                            <div style={styles.kvKey}>eventId</div>
                            <div style={{ ...styles.kvValue, ...styles.mono }}>{event.eventId || '—'}</div>

                            <div style={styles.kvKey}>source</div>
                            <div style={styles.kvValue}><SourceBadge source={event.source} /></div>

                            <div style={styles.kvKey}>shopifyOrderId</div>
                            <div style={{ ...styles.kvValue, ...styles.mono }}>{event.shopifyOrderId || '—'}</div>

                            <div style={styles.kvKey}>shopDomain</div>
                            <div style={styles.kvValue}>{event.shopDomain || '—'}</div>

                            <div style={styles.kvKey}>status</div>
                            <div style={styles.kvValue}><StatusBadge status={event.status} /></div>

                            <div style={styles.kvKey}>state</div>
                            <div style={styles.kvValue}>{event.state !== undefined && event.state !== null ? event.state : '—'}</div>

                            <div style={styles.kvKey}>manualReviewReason</div>
                            <div style={styles.kvValue}>
                                {event.manualReviewReason ? <span style={styles.tag}>{event.manualReviewReason}</span> : '—'}
                            </div>

                            <div style={styles.kvKey}>fortnoxOriginalInvoice</div>
                            <div style={{ ...styles.kvValue, ...styles.mono }}>{event.fortnoxOriginalInvoice || '—'}</div>

                            <div style={styles.kvKey}>fortnoxCreditDoc</div>
                            <div style={{ ...styles.kvValue, ...styles.mono }}>{event.fortnoxCreditDoc || '—'}</div>

                            <div style={styles.kvKey}>fortnoxVoucherNumber</div>
                            <div style={{ ...styles.kvValue, ...styles.mono }}>{event.fortnoxVoucherNumber || '—'}</div>

                            <div style={styles.kvKey}>attempts</div>
                            <div style={styles.kvValue}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                    <span style={styles.tag}>shopifyFetch: {a.shopifyFetch ?? 0}</span>
                                    <span style={styles.tag}>invoiceLookup: {a.invoiceLookup ?? 0}</span>
                                    <span style={styles.tag}>invoiceBooking: {a.invoiceBooking ?? 0}</span>
                                    <span style={styles.tag}>fortnoxApi: {a.fortnoxApi ?? 0}</span>
                                </div>
                            </div>

                            <div style={styles.kvKey}>createdAt</div>
                            <div style={styles.kvValue}>{formatDate(event.createdAt)}</div>

                            <div style={styles.kvKey}>updatedAt</div>
                            <div style={styles.kvValue}>{formatDate(event.updatedAt)}</div>

                            <div style={styles.kvKey}>completedAt</div>
                            <div style={styles.kvValue}>{formatDate(event.completedAt)}</div>
                        </div>
                    </div>

                    <div style={styles.section}>
                        <div style={styles.sectionTitle}>Steps timeline</div>
                        <StepsTimeline steps={event.steps} />
                    </div>

                    <div style={styles.section}>
                        <div style={styles.sectionTitle}>Raw Shopify Payload</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div>
                                <div style={{ fontSize: 12, color: palette.textMuted, marginBottom: 4 }}>refund_line_items</div>
                                <JsonBlock value={event.payload && event.payload.refund_line_items} label="refund_line_items" />
                            </div>
                            <div>
                                <div style={{ fontSize: 12, color: palette.textMuted, marginBottom: 4 }}>transactions</div>
                                <JsonBlock value={event.payload && event.payload.transactions} label="transactions" />
                            </div>
                        </div>
                    </div>
                </div>

                <div style={styles.panelFooter}>
                    <button style={styles.btnGhost} onClick={onClose} disabled={busy}>Close</button>
                    {event.status === 'manual_review' && (
                        <>
                            <button style={styles.btnSuccess} onClick={onMarkResolved} disabled={busy}>Mark manual review resolved</button>
                            <button style={styles.btn} onClick={onReset} disabled={busy}>Retry</button>
                        </>

                    )}

                </div>
            </div>
        </>
    );
}

class Screen extends RouteComponent {
    constructor(props) {
        super(props);
        this.state = {
            items: [],
            total: 0,
            pagination: { page: 0, limit: 10, total: 0, totalPages: 0 },
            loading: false,
            error: null,
            selected: null,
            actionBusy: false,
            shopifyOrderId: ''
        };
    }

    componentDidMount() {
        const params = this._getParams();
        this.setState({ shopifyOrderId: params.shopifyOrderId || '' });
        this._load();
    }

    componentDidUpdate(prevProps) {
        if (this.props[0].location.search !== prevProps[0].location.search) {
            this._load();
        }
    }

    componentWillUnmount() {
        if (this._searchTimer) clearTimeout(this._searchTimer);
    }

    _load = () => {
        if (!this.props.api || !this.props.api['api1']) return;
        this.setState({ loading: true, error: null });
        const query = this._getParams();
        this.props.api['api1'].action(this.props.lang, this.props[0].match, query)
            .then((data) => {
                this.setState({
                    items: Array.isArray(data?.items) ? data.items : [],
                    total: data?.total ?? 0,
                    pagination: data?.pagination || { page: 0, limit: 10, total: 0, totalPages: 0 },
                    loading: false
                });
            })
            .catch((err) => {
                this.setState({
                    loading: false,
                    error: (err && err.message) || 'Failed to load refund events',
                    items: []
                });
            });
    }

    _setFilter = (name, value) => {
        this.updateMultipleParams([
            { name, value: value || null },
            { name: 'page', value: 0 }
        ]);
    }

    _onSearchChange = (e) => {
        const value = e.target.value;
        this.setState({ shopifyOrderId: value });
        if (this._searchTimer) clearTimeout(this._searchTimer);
        this._searchTimer = setTimeout(() => {
            this.updateMultipleParams([
                { name: 'shopifyOrderId', value: value.trim() || null },
                { name: 'page', value: 0 }
            ]);
        }, 300);
    }

    _clearSearch = () => {
        if (this._searchTimer) clearTimeout(this._searchTimer);
        this.setState({ shopifyOrderId: '' });
        this.updateMultipleParams([
            { name: 'shopifyOrderId', value: null },
            { name: 'page', value: 0 }
        ]);
    }

    _onSortClick = (field) => {
        const params = this._getParams();
        const currentField = params.sortField;
        const currentType = parseInt(params.sortType);
        const nextType = currentField === field && currentType === 1 ? -1 : 1;
        this.updateMultipleParams([
            { name: 'sortField', value: field },
            { name: 'sortType', value: nextType },
            { name: 'page', value: 0 }
        ]);
    }

    _sortClass = (field) => {
        const params = this._getParams();
        if (params.sortField !== field) return 'sort';
        return parseInt(params.sortType) === 1 ? 'sort sort-asc' : 'sort sort-desc';
    }

    _onEntriesChange = (e) => {
        this.updateMultipleParams([
            { name: 'entries', value: e.target.value },
            { name: 'page', value: 0 }
        ]);
    }

    _setPage = (p) => this.updateParams('page', p);

    _openDetail = (event) => this.setState({ selected: event });
    _closeDetail = () => this.setState({ selected: null });

    _resetEvent = () => {
        const { selected } = this.state;
        console.log(990)
        if (!selected) return;
        this.setState({ actionBusy: true });
        this._api('reset-event', { id: selected._id }, ({ result, status }) => {
            this.setState({ actionBusy: false });
            if (status >= 200 && status < 300) {
                const next = (result && (result.data || result.event || result)) || selected;
                this.setState({
                    selected: typeof next === 'object' && next._id ? next : {
                        ...selected,
                        status: 'processing',
                        manualReviewReason: null,
                        attempts: { shopifyFetch: 0, invoiceLookup: 0, invoiceBooking: 0, fortnoxApi: 0 },
                        completedAt: null
                    }
                });
                this._load();
            } else {
                Swal.fire({ icon: 'error', title: 'Reset failed', text: (result && result.error) || 'Server error' });
            }
        });
    }

    _markResolved = () => {
        const { selected } = this.state;
        if (!selected) return;
        this.setState({ actionBusy: true });
        this._api('mark-resolved', { id: selected._id }, ({ result, status }) => {
            this.setState({ actionBusy: false });
            if (status >= 200 && status < 300) {
                const next = (result && (result.data || result.event || result)) || selected;
                this.setState({
                    selected: typeof next === 'object' && next._id ? next : { ...selected, status: 'completed' }
                });
                this._load();
            } else {
                Swal.fire({ icon: 'error', title: 'Update failed', text: (result && result.error) || 'Server error' });
            }
        });
    }

    _sortIndicator(field) {
        return (
            <span className={this._sortClass(field)}>
                <Isvg src={sortUp} className="sort-up-icon" />
                <Isvg src={sortDown} className="sort-down-icon" />
            </span>
        );
    }

    render() {
        const params = this._getParams();
        const status = params.status || '';
        const page = Number(params.page) || 0;
        const limit = Number(params.entries) || 10;

        const { items, pagination, loading, error, selected, actionBusy } = this.state;
        const total = pagination.total ?? this.state.total ?? 0;
        const totalPages = Math.max(1, Math.ceil(total / limit));
        const rangeFrom = total === 0 ? 0 : page * limit + 1;
        const rangeTo = Math.min((page + 1) * limit, total);

        const columns = [
            { label: 'Event ID', field: 'eventId' },
            { label: 'Source', field: 'source' },
            { label: 'Shop Domain', field: 'shopDomain' },
            { label: 'Shopify Order ID', field: 'shopifyOrderId' },
            { label: 'Status', field: 'status' },
            { label: 'Manual Review Reason', field: 'manualReviewReason' },
            { label: 'Fortnox Invoice / Credit', field: 'fortnoxOriginalInvoice' },
            { label: 'Created At', field: 'createdAt' },
        ];

        return (
            <div>
                <div className="section-title title-options">
                    <h1>Refund events</h1>
                    <div className='buttons'>
                        <div className="ff-export">
                            <Button
                                loader={loading}
                                className="btn-primary"
                                onClick={this._load}
                            >
                                Refresh
                            </Button>
                        </div>
                    </div>
                </div>

                <div style={styles.subtitle}>Track Shopify refunds and order edits as they flow into Fortnox.</div>

                <div>
                    <Row className="list-builder-wrap">
                        <Col lg="7" style={{ display: 'flex', alignItems: 'center' }}>
                            <FormGroup style={{ marginBottom: 0 }}>
                                <Input
                                    type="select"
                                    style={{ minWidth: 180, height: 42, fontSize: 13 }}
                                    value={status}
                                    onChange={(e) => this._setFilter('status', e.target.value)}
                                >
                                    <option value="">All statuses</option>
                                    <option value="processing">Processing</option>
                                    <option value="completed">Completed</option>
                                    <option value="manual_review">Manual review</option>
                                </Input>
                            </FormGroup>
                        </Col>

                        <Col lg="5" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <FormGroup className="table-filter">
                                <Label>
                                    <Input
                                        type="select"
                                        className="entries"
                                        style={{ minWidth: 62 }}
                                        value={limit}
                                        onChange={this._onEntriesChange}
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </Input>
                                    results per page
                                </Label>
                                <div className="filter-results">
                                    <FormGroup>
                                        <Label style={{ position: 'relative', display: 'inline-block' }}>
                                            <Input
                                                type="text"
                                                placeholder="Search by shopify order id ..."
                                                style={{ marginRight: 0, paddingRight: this.state.shopifyOrderId ? 28 : undefined }}
                                                value={this.state.shopifyOrderId}
                                                onChange={this._onSearchChange}
                                            />
                                            {this.state.shopifyOrderId ? (
                                                <button
                                                    type="button"
                                                    onClick={this._clearSearch}
                                                    aria-label="Clear search"
                                                    style={{
                                                        position: 'absolute',
                                                        right: 8,
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        background: 'transparent',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        color: palette.textMuted,
                                                        fontSize: 18,
                                                        lineHeight: 1,
                                                        padding: 0,
                                                    }}
                                                >
                                                    ×
                                                </button>
                                            ) : null}
                                        </Label>
                                    </FormGroup>
                                </div>
                            </FormGroup>
                        </Col>

                        <Col lg="12" className="table-container">
                            <div className="table-container-scroll">
                                <Table responsive hover className="cursor-row">
                                    <thead>
                                        <tr style={{ whiteSpace: 'nowrap' }}>
                                            {columns.map((col) => (
                                                <th
                                                    key={col.field}
                                                    className="sort-enabled"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => this._onSortClick(col.field)}
                                                >
                                                    {col.label} {this._sortIndicator(col.field)}
                                                </th>
                                            ))}
                                            <th className="action-td"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {error && (
                                            <tr>
                                                <td colSpan={columns.length + 1} style={{ textAlign: 'center', color: palette.danger, padding: 40 }}>
                                                    {error}
                                                </td>
                                            </tr>
                                        )}
                                        {!error && items.length === 0 && !loading && (
                                            <tr>
                                                <td colSpan={columns.length + 1} style={{ textAlign: 'center', color: palette.textMuted, padding: 40 }}>
                                                    No refund events found.
                                                </td>
                                            </tr>
                                        )}
                                        {items.map((evt) => (
                                            <tr key={evt._id || evt.eventId}>
                                                <td style={styles.mono} title={evt.eventId || ''}>{truncate(evt.eventId, 14)}</td>
                                                <td><SourceBadge source={evt.source} /></td>
                                                <td>{evt.shopDomain || '—'}</td>
                                                <td style={styles.mono}>{evt.shopifyOrderId || '—'}</td>
                                                <td><StatusBadge status={evt.status} /></td>
                                                <td>
                                                    {evt.manualReviewReason ? <span style={styles.tag}>{evt.manualReviewReason}</span> : <span style={{ color: palette.textMuted }}>—</span>}
                                                </td>
                                                <td style={styles.mono}>
                                                    {evt.fortnoxOriginalInvoice || evt.fortnoxCreditDoc ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                            {evt.fortnoxOriginalInvoice && <span>inv: {evt.fortnoxOriginalInvoice}</span>}
                                                            {evt.fortnoxCreditDoc && <span>credit: {evt.fortnoxCreditDoc}</span>}
                                                        </div>
                                                    ) : <span style={{ color: palette.textMuted }}>—</span>}
                                                </td>
                                                <td>{formatDate(evt.createdAt)}</td>
                                                <td className="action-td">
                                                    <button style={styles.btnGhost} onClick={() => this._openDetail(evt)}>View</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Col>

                        <Col lg="6">
                            <ReactPaginate
                                previousLabel={'<'}
                                nextLabel={'>'}
                                breakLabel={'...'}
                                breakClassName={'page-item disabled'}
                                breakLinkClassName={'page-link disabled'}
                                pageCount={totalPages}
                                marginPagesDisplayed={1}
                                pageRangeDisplayed={2}
                                onPageChange={(p) => this._setPage(p.selected)}
                                containerClassName={'pagination'}
                                subContainerClassName={'pages pagination'}
                                pageClassName={'page-item'}
                                pageLinkClassName={'page-link'}
                                previousClassName={'page-item-next'}
                                previousLinkClassName={'page-link'}
                                nextClassName={'page-item-next'}
                                nextLinkClassName={'page-link'}
                                activeClassName={'active'}
                                forcePage={Math.max(0, page)}
                            />
                        </Col>
                        <Col lg="6">
                            <span className="news-counter">
                                Shows <span className="colored-green">{total ? rangeFrom : 0}</span> to <span className="colored-green">{rangeTo}</span> of <span className="colored-blue">{total}</span> items
                            </span>
                        </Col>
                    </Row>
                </div>

                {selected && (
                    <DetailPanel
                        event={selected}
                        onClose={this._closeDetail}
                        onReset={this._resetEvent}
                        onMarkResolved={this._markResolved}
                        busy={actionBusy}
                    />
                )}
            </div>
        );
    }
}

export default Page(Screen);
